import {cva} from 'class-variance-authority'

/**
 * MenuItem (v3, MUI-aligned). The shared atom rendered by both the floating
 * `Menu` dropdown and the persistent `Sidebar` rail.
 *
 * Row heights (per Figma): `dense` 32px (14px text) · `size` 36px (`md`, default)
 * or 48px (`lg`, the roomier nav rail) · `smallScreen` 48px touch target (16px
 * text). Precedence is `smallScreen` > `dense` > `size`.
 *
 * - `selected` — filled teal `action/active` pill, white bold text (was Sidebar.Item
 *   `active`). On a `dark` rail the pill inverts to the pale `action/active-inverse`
 *   fill with dark content — a teal pill on deep teal has too little separation,
 *   and the pale fill would collide with `disabled-background` on a light surface.
 * - `color` — the surface the row sits on: `light` (white/subdued app surface,
 *   dark text) or `dark` (the deep-teal `primary-contrast` brand rail, white
 *   text). Inherited from `Sidebar color` via `MenuColorProvider`.
 */
export const MenuItemVariantsFactory = cva(
  'flex w-full items-center gap-2 rounded-lg px-3 font-sans transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset [&_svg]:size-6 [&_svg]:shrink-0',
  {
    variants: {
      color: {light: '', dark: ''},
      size: {md: '', lg: ''},
      dense: {true: '', false: ''},
      smallScreen: {true: '', false: ''},
      selected: {
        true: 'bg-action-active font-bold text-white',
        false: '',
      },
      disabled: {
        true: 'cursor-not-allowed',
        false: 'cursor-pointer',
      },
    },
    compoundVariants: [
      // Row sizing — smallScreen (48px) wins over dense (32px), which wins over `size`.
      {smallScreen: true, class: 'py-3 text-[16px] leading-normal tracking-[0.15px]'},
      {smallScreen: false, dense: true, class: 'py-1 text-[14px] leading-6 tracking-[0.17px]'},
      {
        smallScreen: false,
        dense: false,
        size: 'md',
        class: 'py-1.5 text-[16px] leading-normal tracking-[0.15px]',
      },
      // `lg` — the 48px nav-rail row (same metrics as the smallScreen target).
      {
        smallScreen: false,
        dense: false,
        size: 'lg',
        class: 'py-3 text-[16px] leading-normal tracking-[0.15px]',
      },
      // Resting foreground per surface (the `selected` pill supplies its own white).
      {color: 'light', selected: false, class: 'text-text-primary'},
      {color: 'dark', selected: false, class: 'text-white'},
      // Selected pill on the brand rail: inverted to a pale fill with dark content,
      // so it reads as a bright chip on #004E56 instead of teal-on-teal.
      {
        color: 'dark',
        selected: true,
        class: 'bg-action-active-inverse text-text-primary',
      },
      // Hover only when interactive and not already selected — a white scrim on
      // the brand rail, the standard teal overlay on light surfaces.
      {color: 'light', selected: false, disabled: false, class: 'hover:bg-action-hover'},
      {color: 'dark', selected: false, disabled: false, class: 'hover:bg-white/10'},
      // Disabled foreground last so it wins over the resting color (via `cn`'s twMerge).
      {color: 'light', disabled: true, class: 'text-text-disabled'},
      {color: 'dark', disabled: true, class: 'text-white/40'},
    ],
    defaultVariants: {
      color: 'light',
      size: 'md',
      dense: false,
      smallScreen: false,
      selected: false,
      disabled: false,
    },
  },
)

/**
 * Menu surface (floating dropdown card).
 * - `floating` — all corners rounded (standalone popover).
 * - `form` — bottom corners only (attached under a field/Select).
 */
export const MenuVariantsFactory = cva(
  'flex min-w-[171px] flex-col overflow-hidden bg-background p-1 shadow-elevation-1',
  {
    variants: {
      variant: {
        floating: 'rounded-lg',
        form: 'rounded-b-lg',
      },
    },
    defaultVariants: {
      variant: 'floating',
    },
  },
)
